<template>
  <div class="legal-page">
    <div class="legal-container">
      <!-- Hero Section -->
      <div class="legal-hero">
        <h1>Terms & Conditions</h1>
        <p class="lead">Terms of service for using REGIIS COSMETICS</p>
      </div>

      <!-- Main Content -->
      <div class="legal-content">
        <!-- Introduction -->
        <section class="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p><strong>Effective Date:</strong> December 2024</p>
          <p>By accessing and using REGIIS COSMETICS ("the Website"), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.</p>
        </section>

        <!-- Account Terms -->
        <section class="legal-section">
          <h2>2. Account Registration</h2>
          <p>To make purchases on REGIIS COSMETICS, you may need to create an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and update your information</li>
            <li>Keep your password secure and confidential</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized use</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that violate these terms or are used for fraudulent purposes.</p>
        </section>

        <!-- Use of Service -->
        <section class="legal-section">
          <h2>3. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Website for any illegal or unauthorized purpose</li>
            <li>Violate any laws in your jurisdiction</li>
            <li>Transmit viruses, malware, or harmful code</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Scrape, copy, or reproduce Website content without permission</li>
            <li>Impersonate any person or entity</li>
            <li>Interfere with or disrupt the Website's functionality</li>
          </ul>
        </section>

        <!-- Product Information -->
        <section class="legal-section">
          <h2>4. Product Descriptions & Pricing</h2>
          <p>We strive to provide accurate product descriptions and pricing. However:</p>
          <ul>
            <li>Product images are for illustration purposes and may vary slightly</li>
            <li>Prices are subject to change without notice</li>
            <li>We reserve the right to correct pricing errors</li>
            <li>Product availability is not guaranteed</li>
            <li>Color representations depend on your device display</li>
          </ul>
          <p>If a pricing error occurs, we will notify you and give you the option to cancel or proceed at the correct price.</p>
        </section>

        <!-- Orders & Payment -->
        <section class="legal-section">
          <h2>5. Orders & Payment</h2>
          <p>When you place an order:</p>
          <ul>
            <li>You make an offer to purchase products at the listed price</li>
            <li>We reserve the right to accept or decline any order</li>
            <li>Order confirmation email does not constitute acceptance</li>
            <li>Acceptance occurs when items are shipped or made available</li>
            <li>Payment must be received before order processing</li>
            <li>All prices are in Nigerian Naira (₦)</li>
          </ul>

          <div class="info-box">
            <h3>Payment Security</h3>
            <p>We use secure payment processing partners. We do not store your complete credit card information on our servers.</p>
          </div>
        </section>

        <!-- Intellectual Property -->
        <section class="legal-section">
          <h2>6. Intellectual Property Rights</h2>
          <p>All content on REGIIS COSMETICS, including text, graphics, logos, images, and software, is the property of REGIIS COSMETICS or its licensors and is protected by copyright, trademark, and other intellectual property laws.</p>
          <p>You may not:</p>
          <ul>
            <li>Reproduce, distribute, or display our content without permission</li>
            <li>Use our trademarks or branding</li>
            <li>Create derivative works from our content</li>
            <li>Use our content for commercial purposes</li>
          </ul>
        </section>

        <!-- User Content -->
        <section class="legal-section">
          <h2>7. User-Generated Content</h2>
          <p>If you submit reviews, comments, or other content:</p>
          <ul>
            <li>You grant us a non-exclusive, worldwide license to use your content</li>
            <li>You represent that you own or have rights to the content</li>
            <li>Your content must not violate any laws or third-party rights</li>
            <li>We reserve the right to remove inappropriate content</li>
            <li>You remain responsible for your content</li>
          </ul>
        </section>

        <!-- Disclaimers -->
        <section class="legal-section">
          <h2>8. Disclaimers & Warranties</h2>
          <p>The Website and products are provided "as is" without warranties of any kind. We disclaim all warranties, express or implied, including:</p>
          <ul>
            <li>Merchantability and fitness for a particular purpose</li>
            <li>Non-infringement of third-party rights</li>
            <li>Uninterrupted or error-free service</li>
            <li>Accuracy or reliability of information</li>
          </ul>
          <p>We do not warrant that the Website will meet your requirements or that defects will be corrected.</p>
        </section>

        <!-- Limitation of Liability -->
        <section class="legal-section">
          <h2>9. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, REGIIS COSMETICS shall not be liable for:</p>
          <ul>
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of profits, revenue, data, or use</li>
            <li>Damage to reputation or goodwill</li>
            <li>Costs of replacement goods or services</li>
          </ul>
          <p>Our total liability for any claim shall not exceed the amount you paid for the product(s) in question.</p>
        </section>

        <!-- Force Majeure -->
        <section class="legal-section">
          <h2>10. Force Majeure</h2>
          <p>We are not liable for delays or failures caused by events beyond our reasonable control, including:</p>
          <ul>
            <li>Natural disasters, war, or terrorism</li>
            <li>Government actions or regulations</li>
            <li>Labor disputes or strikes</li>
            <li>Power outages or internet failures</li>
            <li>Supplier failures</li>
          </ul>
        </section>

        <!-- Indemnification -->
        <section class="legal-section">
          <h2>11. Indemnification</h2>
          <p>You agree to indemnify and hold harmless REGIIS COSMETICS, its affiliates, and employees from any claims, damages, or expenses arising from:</p>
          <ul>
            <li>Your use of the Website</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Your user-generated content</li>
          </ul>
        </section>

        <!-- Termination -->
        <section class="legal-section">
          <h2>12. Termination</h2>
          <p>We may terminate or suspend your account and access to the Website at any time, without prior notice, for:</p>
          <ul>
            <li>Violation of these Terms</li>
            <li>Fraudulent activity</li>
            <li>At our sole discretion</li>
          </ul>
          <p>Upon termination, your right to use the Website will immediately cease.</p>
        </section>

        <!-- Governing Law -->
        <section class="legal-section">
          <h2>13. Governing Law & Disputes</h2>
          <p>These Terms are governed by the laws of the Federal Republic of Nigeria. Any disputes shall be resolved through:</p>
          <ul>
            <li>Good faith negotiation between parties</li>
            <li>Mediation or arbitration if negotiation fails</li>
            <li>Nigerian courts as a last resort</li>
          </ul>
        </section>

        <!-- Changes to Terms -->
        <section class="legal-section">
          <h2>14. Changes to Terms</h2>
          <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Website. Your continued use after changes constitutes acceptance of the new Terms.</p>
          <p>We encourage you to review these Terms periodically.</p>
        </section>

        <!-- Severability -->
        <section class="legal-section">
          <h2>15. Severability</h2>
          <p>If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.</p>
        </section>

        <!-- Agreement -->
        <section class="legal-section">
          <p><strong>By using REGIIS COSMETICS, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.</strong></p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Terms',
  mounted() {
    window.scrollTo(0, 0)
  }
}
</script>
